import React, { useMemo, useCallback, useState } from 'react';
import { Button, Stack } from '@mantine/core';
import { MasterTradeParams } from 'app/models/master-trade-params';
import MainTable from 'app/views/components/Table/MainTable';
import { GridPaginationStorage, GridColumnVisibilityStorage, useGridPagination, useGridColumnVisibility } from 'app/utils/grid-sorting-storage';
import { columnDefs } from './sub-trade-entry-panel-columnDefs';
import { FacilityAllocationHandlers, SubTradeParams } from 'app/models/sub-trade-params';
import { useSelector } from 'react-redux';
import { getSubTrades } from 'app/state/ducks/sub-trades/selectors';
import { getLockedFacilities } from 'app/state/ducks/facilities/selectors';
// import { getFacilityAmounts } from 'app/state/ducks/facilities/selectors';
import { getBusinessDate } from 'app/state/ducks/business-date/selectors';
import SubTradeEntryPanelAllocations from './sub-trade-entry-panel-allocations';
import { getTradedFacilities } from 'app/state/ducks/traded-facilities/selectors';
import { RefinedSubTrade } from 'app/models/traded-facilities-params';
import { NewSelectInput } from 'app/views/components/inputs/selects/NewSelectInput';
import UpdateSubTradeAllocation from './update-sub-trade-allocation';
import { getRefinedSubTrades } from '../../utils/master-trade-util';
import { getCalendars } from 'app/state/ducks/holiday-calendars/selectors';
// import { useWireInstructionValidation } from 'app/hooks/useWireInstructionValidation';
import { getTradeDetailFullySignedAa } from 'app/state/ducks/trade-detail/selectors';
import { AAMetadataResponse } from 'app/models/aa-metadata-params';
import { useTradePageDeals, useTradePageEntities, useTradePageFacilities, useTradePageWireInstructions } from 'app/views/pages/trade-entry/detail-trade/use-trade-page-lookups';

interface DetailPanelProps {
  row: MasterTradeParams;
  onRegisterFacilityActions?: (handlers: FacilityAllocationHandlers | null) => void;
}

const tradeViewColumns = ['All', 'Settled', 'Open', 'Canceled', 'Terminated'];

const SubTradeEntryPanel: React.FC<DetailPanelProps> = ({ row, onRegisterFacilityActions }) => {
  const subTrades: SubTradeParams[] = useSelector(getSubTrades);
  const allEntities = useTradePageEntities();
  const facilities = useTradePageFacilities();
  const allTradedFacilities = useSelector(getTradedFacilities);
  const businessDate = useSelector(getBusinessDate);
  const allDeals = useTradePageDeals();
  const [statusTable, setStatusTable] = useState<string>('All');
  const [showSubTradeTable, setShowSubTradeTable] = useState<boolean>(true);
  const holidayCalendars = useSelector(getCalendars)
  const wireInstructions = useTradePageWireInstructions();
  // const facilityAmounts = useSelector(getFacilityAmounts);
  // The fully signed AA metadata (the settlement-date override) arrives with
  // the tab's one read — the Daml choice's rule, run in its statement
  // (ADR_LOS-030 step 2) — so there is nothing left to load here. The sub
  // trades' attachments arrive with it too (they fed loadByContract).
  const fullySignedAa = useSelector(getTradeDetailFullySignedAa);
  const aaMetadata: AAMetadataResponse = useMemo(() => ({ success: true, data: fullySignedAa }), [fullySignedAa]);
  const isLoadingAAMetadata = false;
  const lockedFacilities = useSelector(getLockedFacilities)
  const allFacilities = useMemo(() => [...facilities, ...lockedFacilities], [facilities, lockedFacilities]);


  const { pagination, handlePaginationChange } = useGridPagination(GridPaginationStorage.subTradeEntry);
  const { columnVisibility, handleColumnVisibilityChange } = useGridColumnVisibility(GridColumnVisibilityStorage.subTradeEntry);



  // Compute refined sub-trades (includes wire instructions for validation)
  const refinedSubTrades = useMemo(() => {
    return getRefinedSubTrades(row, subTrades, allDeals, allEntities, allTradedFacilities, allFacilities, businessDate, holidayCalendars, wireInstructions, aaMetadata, isLoadingAAMetadata)
  }, [subTrades, allDeals, allEntities, allTradedFacilities, allFacilities, businessDate, holidayCalendars, row, wireInstructions, aaMetadata, isLoadingAAMetadata]);

  // TEMPORARILY DISABLED per #6361 - Wire instruction validation hook
  // This hook triggers expensive async calculations for every row causing UI lag.
  // Will be replaced with PQS-based solution or redesigned approach.
  // const { refinedSubTrades: validatedRefinedSubTrades } = useWireInstructionValidation({
  //   baseRefinedSubTrades: refinedSubTrades,
  //   wireInstructions,
  //   allDeals,
  //   facilityAmounts,
  // });

  // Use refined subtrades directly until performance is fixed
  const validatedRefinedSubTrades = refinedSubTrades;

  const showUpdateAllocations = useMemo(() => {
    if (!row || !row.buyerId || !row.sellerId) return false;
    if (row.status !== 'Open') return false;
    if (!subTrades) return true;
    const st = subTrades.filter(st => st.masterTradeId.id === row.id)
    if (st.length != 1) return true;
    return st[0].buyerId?.id != row.buyerId.id || st[0].sellerId?.id != row.sellerId.id;
  }, [row, subTrades]
  )


  const renderDetailPanelAllocations = useCallback(
    (row: { original: RefinedSubTrade; getIsExpanded: () => boolean }) => {
      if (!row.getIsExpanded()) return null;
      return <SubTradeEntryPanelAllocations refinedSubTrade={row.original} isExpanded={row.getIsExpanded()} />;
    },
    []
  );

  const filteredSubTrades = useMemo(() => {
    return validatedRefinedSubTrades
      .filter(trade =>
        statusTable.toLowerCase() === 'all'
          ? true
          : trade.status?.toLowerCase() === statusTable.toLowerCase()
      )
      .sort((a, b) => (a.systemId || '').localeCompare(b.systemId || ''));
  }, [validatedRefinedSubTrades, statusTable]);


  return (
    <>
      <Stack w="100%" className="sectionDetailTradeBody noHeader">
        <Stack w="100%" className="sectionRemoveGap sectionBody">
          <div className="allocation-div" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: '96%' }}>
            <div className="allocation-text">Allocations</div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {showUpdateAllocations && showSubTradeTable && (<Button
                className="update-allocation-button"
                onClick={() => {
                  setShowSubTradeTable(false);
                }}
              >
                <div className="update-allocation-button-text">Update Allocations</div>
              </Button>
              )}
              <NewSelectInput
                wrapperType="trade"
                label="Status"
                data={tradeViewColumns}
                w="164px"
                h="42px"
                placeholder="Status"
                value={statusTable}
                onChange={(value) => setStatusTable(value ?? 'All')}
              />
            </div>
          </div>

          <Stack w="100%" className="sectionDetailTradeBody" style={{ padding: '10px' }}>
            {showSubTradeTable ? (
              <MainTable
                tableName="AA"
                columnDefs={columnDefs}
                withPagination={true}
                initialPagination={pagination}
                onPaginationChange={handlePaginationChange}
                initialColumnVisibility={columnVisibility}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                renderDetailPanel={({ row }) => renderDetailPanelAllocations(row)}
                data={filteredSubTrades ?? []}
                setRow={() => { }}
                enableTopToolbar={false}
                headerBackgroundColor="#F0EEEE"
                enableColumnOrdering={false}
                enableColumnResizing={false}
                enableGrouping={false}
                enableColumnFilters={false}
                enableColumnActions={false}
                enableFilters={false}
                enableSorting={false}
                enableFullScreenToggle={false}
                enableDensityToggle={false}
                enableHiding={false}
                border={true}
                fromTradeEntry={false}
                isAccordionTable={true}
                singleExpand={true}
              />
            ) : <UpdateSubTradeAllocation row={row} refinedSubTrades={refinedSubTrades} closeUpdateAllocation={() => setShowSubTradeTable(true)} onRegisterFacilityActions={onRegisterFacilityActions} />}
          </Stack>
        </Stack>
      </Stack >
    </>
  );
};

export default SubTradeEntryPanel;


